import { parseAlgorithm } from '../core/moves.js';
import {
  createSolverDiagnostic4x4,
  serializeSolverError,
  SOLVER_DIAGNOSTIC_STAGE_4X4,
} from './solver-diagnostics-4x4.js';
import { SolverExecutor } from './solver-executor.js';
import solverWorker3x3Url from './solver-worker-3x3.js?worker&url';

export const SOLVER_STATUS = Object.freeze({
  VALIDATING: 'validating',
  INITIALIZING: 'initializing',
  SOLVING: 'solving',
  COMPATIBILITY: 'compatibility',
  COMPLETE: 'complete',
});

export const SOLVER_STATUS_TEXT = Object.freeze({
  [SOLVER_STATUS.VALIDATING]: '正在校验状态',
  [SOLVER_STATUS.INITIALIZING]: '正在初始化求解器',
  [SOLVER_STATUS.SOLVING]: '正在计算方案',
  [SOLVER_STATUS.COMPATIBILITY]: '正在使用兼容模式求解',
  [SOLVER_STATUS.COMPLETE]: '求解完成',
});

export class SolverError extends Error {
  constructor(code, message, cause) {
    super(message, { cause });
    this.name = 'SolverError';
    this.code = code;
    this.workerStage = cause?.workerStage ?? null;
    this.workerStack = cause?.workerStack ?? cause?.stack ?? '';
    this.resourceUrl = cause?.resourceUrl ?? null;
    this.diagnostics = [];
    this.inputDiagnostics = null;
  }
}

function noopStatus(_status) {}
function noopDiagnostic(_event) {}

async function defaultLoader() {
  const [{ KPattern }, { cube3x3x3 }] = await Promise.all([
    import('cubing/kpuzzle'),
    import('cubing/puzzles'),
  ]);
  return { KPattern, cube3x3x3 };
}

function now() {
  return globalThis.performance?.now?.() ?? Date.now();
}

function nextPaint() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve());
    else setTimeout(resolve, 0);
  });
}

function withTimeout(promise, timeoutMs) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new SolverError(
      'TIMEOUT',
      `求解计算超过 ${Math.round(timeoutMs / 1000)} 秒，已停止等待`,
    )), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

function workerErrorToSolverError(cause, label = '3×3求解') {
  if (cause instanceof SolverError) return cause;
  const stage = cause?.workerStage ? `，阶段：${cause.workerStage}` : '';
  const name = cause?.name && cause.name !== 'Error' ? `${cause.name}: ` : '';
  return new SolverError(
    cause?.code ?? 'SOLVER_RUNTIME_ERROR',
    `${label}未完成${stage}。${name}${cause?.message ?? String(cause)}`,
    cause,
  );
}

export class SolverClient {
  constructor({
    loader = null,
    workerFactory = () => new Worker(solverWorker3x3Url, { type: 'module' }),
    timeoutMs = 60000,
    readyTimeoutMs = 5000,
    executor = null,
  } = {}) {
    this.loader = loader ?? defaultLoader;
    this.customLoader = Boolean(loader);
    this.timeoutMs = timeoutMs;
    this.initializationPromise = null;
    this.runtime = null;
    this.workerUrl = new URL(solverWorker3x3Url, import.meta.url);
    this.executor = executor ?? new SolverExecutor({
      label: '3×3',
      workerUrl: this.workerUrl.href,
      workerType: 'module',
      workerFactory,
      readyTimeoutMs,
      timeoutMs,
      fallback: (payload, hooks) => this.#solveOnMainThread(payload, hooks),
    });
  }

  async #solveOnMainThread({ patternData }, { onStatus, onDiagnostic, startedAtMs }) {
    await nextPaint();
    const emit = (stage, details = {}) => onDiagnostic(createSolverDiagnostic4x4(stage, {
      source: 'main-fallback-3x3',
      startedAtMs,
      details,
    }));
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.DEPENDENCY_LOAD_START, {
      module: 'cubing-min2phase-adapter',
      execution: 'main-thread',
    });
    const direct = await import('../../node_modules/cubing/dist/lib/cubing/chunks/cube-path-min2phase-adapter.js');
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.DEPENDENCY_LOAD_END, {
      module: 'cubing-min2phase-adapter',
      execution: 'main-thread',
    });
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.WASM_LOAD_START, { required: false });
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.WASM_LOAD_END, {
      required: false,
      implementation: 'javascript-min2phase',
    });
    onStatus(SOLVER_STATUS.INITIALIZING);
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.SOLVER_INIT_START, { execution: 'main-thread' });
    const initStartedAt = now();
    await direct.initialize();
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.SOLVER_INIT_END, {
      execution: 'main-thread',
      durationMs: Math.round(now() - initStartedAt),
    });
    onStatus(SOLVER_STATUS.SOLVING);
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.SOLVE_START, { execution: 'main-thread', solver: 'min2phase' });
    const solveStartedAt = now();
    const formula = direct.solvePatternData(patternData).trim();
    emit(SOLVER_DIAGNOSTIC_STAGE_4X4.SOLVE_END, {
      execution: 'main-thread',
      solver: 'min2phase',
      durationMs: Math.round(now() - solveStartedAt),
    });
    return { formula };
  }

  async initialize(onStatus = noopStatus) {
    if (this.runtime) return this.runtime;
    if (!this.initializationPromise) {
      this.initializationPromise = (async () => {
        const loaded = await this.loader();
        const kpuzzle = await loaded.cube3x3x3.kpuzzle();
        if (this.customLoader) {
          onStatus(SOLVER_STATUS.INITIALIZING);
          await loaded.solvePattern(kpuzzle.defaultPattern().applyMove('R'));
        }
        this.runtime = { ...loaded, kpuzzle };
        return this.runtime;
      })().catch((cause) => {
        this.initializationPromise = null;
        if (cause instanceof SolverError) throw cause;
        throw new SolverError(
          'INITIALIZATION_FAILED',
          `求解器依赖初始化失败。${cause?.name ?? 'Error'}: ${cause?.message ?? cause}`,
          cause,
        );
      });
    }
    return withTimeout(this.initializationPromise, this.timeoutMs);
  }

  async solve(patternData, {
    onStatus = noopStatus,
    onDiagnostic = noopDiagnostic,
    startedAtMs = Date.now(),
  } = {}) {
    const startedAt = now();
    const diagnostics = [];
    const emitDiagnostic = (event) => {
      diagnostics.push(event);
      onDiagnostic(event);
    };
    try {
      const runtime = await this.initialize(onStatus);
      const pattern = new runtime.KPattern(runtime.kpuzzle, patternData);
      let rawFormula;
      let execution = { compatibilityMode: false };
      if (this.customLoader) {
        onStatus(SOLVER_STATUS.SOLVING);
        const algorithm = await withTimeout(runtime.solvePattern(pattern), this.timeoutMs);
        rawFormula = algorithm.toString().trim();
      } else {
        execution = await this.executor.execute({ patternData }, {
          onStatus,
          onDiagnostic: emitDiagnostic,
          startedAtMs,
        });
        rawFormula = execution.formula.trim();
      }
      const moves = parseAlgorithm(rawFormula);
      const formula = moves.join(' ');
      const solvedPattern = pattern.applyAlg(formula);
      if (!solvedPattern.isIdentical(runtime.kpuzzle.defaultPattern())) {
        throw new SolverError(
          'RESULT_MISMATCH',
          '求解器返回的公式未能还原当前状态，已拒绝播放该结果',
        );
      }
      onStatus(SOLVER_STATUS.COMPLETE);
      return {
        formula,
        moves,
        moveCount: moves.length,
        durationMs: Math.round(now() - startedAt),
        compatibilityMode: Boolean(execution.compatibilityMode),
        workerError: execution.workerError ?? null,
        diagnostics,
      };
    } catch (cause) {
      const error = workerErrorToSolverError(cause);
      error.diagnostics = [...diagnostics];
      if (!diagnostics.some((item) => item.stage === SOLVER_DIAGNOSTIC_STAGE_4X4.WORKER_ERROR)) {
        emitDiagnostic(createSolverDiagnostic4x4(SOLVER_DIAGNOSTIC_STAGE_4X4.WORKER_ERROR, {
          source: 'main',
          startedAtMs,
          details: serializeSolverError(error, { resourceUrl: error.resourceUrl }),
        }));
      }
      throw error;
    }
  }

  dispose() {
    this.executor.dispose();
  }
}
